import { SimulationVariable } from '../simulation-variable';
import { UnitOperationConstraints } from './unit-operation-constraints';
import { unitOperationsConfig } from '../../_config/unit-operations.config';
import { Case } from '../case';
import { Quantity } from '../../_config/quantity.enum';
import { GasContributorUnitOperation } from './gas-contributor-unit-operation';
import { KpiProvider } from '../_case-study/kpi-provider';
import { SimulationVariableName } from '../../_config/simulation-variable-name.enum';
import { ParameterProvider } from '../_case-study/parameter-provider';

declare let unitConverter: any;

export class HydrogenPlant extends GasContributorUnitOperation implements KpiProvider, ParameterProvider {
  category = unitOperationsConfig.hydrogenPlant.key;

  constraints: UnitOperationConstraints;

  hydrogenGHGIntensity: SimulationVariable;
  hydrogenProduction: SimulationVariable;
  maximumHydrogenProduction: SimulationVariable;
  constructor(unitOperation: any, ownerCase: Case) {
    super(unitOperation, ownerCase);
    this.initValues(unitOperation);
    this.hydrogenGHGIntensity = new SimulationVariable(unitOperation.hydrogenGHGIntensity, this.ownerCase, this);
    this.hydrogenProduction = new SimulationVariable(unitOperation.hydrogenProduction, this.ownerCase, this);
    this.maximumHydrogenProduction = new SimulationVariable(
      unitOperation.maximumHydrogenProduction,
      this.ownerCase,
      this
    );
    this.setSimulationVariableNames();
  }

  getAvailableKpis(): SimulationVariable[] {
    if (this.ownerCase.multiPeriodEnabled) {
      return [
        this.maximumHydrogenProduction,
        this.hydrogenProduction,
        this.hydrogenGHGIntensity,
        this.ghgEmissions,
        this.opexTotal,
        this.capexTotal,
      ];
    }
    return [
      this.maximumHydrogenProduction,
      this.hydrogenProduction,
      this.hydrogenGHGIntensity,
      this.ghgEmissions,
      this.steamUse900,
      this.steamMake900,
      this.steamMake600,
      this.steamUse600,
      this.steamMake150,
      this.steamUse150,
      this.steamMake50,
      this.steamUse50,
      this.fuelGasHeatingValue,
      this.fuelGasEmissionFactor,
      this.energyFlow,
      this.flowrate,
      this.opexTotal,
      this.capexTotal,
    ];
  }

  getAvailableParametricStudyParameters(): SimulationVariable[] {
    return [this.ghgIntensity, this.opexVar, this.opexGasDiesel, this.opexPower, this.opexFixed, this.capexAmortized];
  }

  getAvailableParameters() {
    return undefined;
  }

  getAlternativeGhgIntensity(): SimulationVariable {
    return undefined;
  }

  override initValues(unitOperation: any | HydrogenPlant): void {
    super.initValues(unitOperation);
  }

  override setQuantities() {
    super.setQuantities();
    this.hydrogenGHGIntensity.setQuantity(Quantity.MASS_PER_STD_H2_VOLUME);
    this.hydrogenProduction.setQuantity(Quantity.MOLEFLOWRATE);
    this.maximumHydrogenProduction.setQuantity(Quantity.MOLEFLOWRATE);
  }

  override dePersist(sup: any) {
    super.dePersist(sup);
    this.hydrogenGHGIntensity = this.getSimulationVariableSafe(sup.hydrogenGHGIntensity);
    this.hydrogenProduction = this.getSimulationVariableSafe(sup.hydrogenProduction);
    this.maximumHydrogenProduction = this.getSimulationVariableSafe(sup.maximumHydrogenProduction);
    this.setQuantities();
    this.setDefaultValues();
  }

  override setDefaultValues(): void {
    if (this.hydrogenGHGIntensity.isUndefined()) {
      this.hydrogenGHGIntensity.setDefaultValue(0.0);
    }
    if (this.energyFlow.isUndefined()) {
      this.energyFlow.setDefaultValue(0.0, unitConverter.units.Energyrate.GJ_H);
    }

    super.setDefaultValues();
  }

  constraintValueDefined() {
    return this.constraints.anyConstraintValueDefined();
  }

  override setSimulationVariableNames() {
    super.setSimulationVariableNames();
    this.maximumHydrogenProduction.setName(SimulationVariableName.MAXIMUM_HYDROGEN_PRODUCTION);
    this.hydrogenProduction.setName(SimulationVariableName.HYDROGEN_PRODUCTION);
    this.hydrogenGHGIntensity.setName(SimulationVariableName.PROCESS_GHG_INTENSITY);
    this.fuelGasHeatingValue.setName(SimulationVariableName.FUEL_GAS_HEATING_VALUE);
    this.fuelGasEmissionFactor.setName(SimulationVariableName.FUEL_GAS_EMISSION_FACTOR);
    this.energyFlow.setName(SimulationVariableName.ENERGY_FLOW);
    this.flowrate.setName(SimulationVariableName.FUEL_GAS_FLOWRATE);
  }
}
